import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { errorToast, successToast } from "@telluscompany/design-system";
import { useLocalization } from "@i18n";
import {
  axiosDeleteData,
  axiosPostData,
  axiosPutData,
} from "@shared/api/clients";

import type {
  CreateStructureRequest,
  CreateStructureResponse,
  UpdateStructureRequest,
  UpdateStructureResponse,
} from "./structures.types";

const STRUCTURES_QUERY_KEY = "structures";

const getStructuresEndpoint = (tenantId: string) =>
  `v1/managerial/tenants/${tenantId}/structures`;

const getCreateStructureEndpoint = (tenantId: string, complexId: string) =>
  `v1/managerial/tenants/${tenantId}/complexes/${complexId}/structures`;

export const createStructure = async (
  tenantId: string,
  complexId: string,
  payload: CreateStructureRequest,
): Promise<CreateStructureResponse> => {
  return axiosPostData<CreateStructureResponse, CreateStructureRequest>(
    getCreateStructureEndpoint(tenantId, complexId),
    payload,
  );
};

export const useCreateStructureMutation = (tenantId: string) => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: ({
      complexId,
      name,
    }: {
      complexId: string;
      name: string;
    }) => createStructure(tenantId, complexId, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [STRUCTURES_QUERY_KEY, tenantId],
        exact: false,
      });
      successToast({
        description: translate("common.createSuccess"),
      });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorCreating"),
      });
    },
  });
};

export const updateStructure = async (
  tenantId: string,
  structureId: string,
  payload: UpdateStructureRequest,
): Promise<UpdateStructureResponse> => {
  return axiosPutData<UpdateStructureResponse, UpdateStructureRequest>(
    `${getStructuresEndpoint(tenantId)}/${structureId}`,
    payload,
  );
};

export const useUpdateStructureMutation = (
  tenantId: string,
  structureId: string,
) => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: (payload: UpdateStructureRequest) =>
      updateStructure(tenantId, structureId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [STRUCTURES_QUERY_KEY, tenantId],
        exact: false,
      });
      successToast({
        description: translate("common.editSuccess"),
      });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorEditing"),
      });
    },
  });
};

export const deleteStructure = async (
  tenantId: string,
  structureId: string,
): Promise<void> => {
  await axiosDeleteData<void>(
    `${getStructuresEndpoint(tenantId)}/${structureId}`,
  );
};

export const useDeleteStructureMutation = (tenantId: string) => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: (structureId: string) =>
      deleteStructure(tenantId, structureId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [STRUCTURES_QUERY_KEY, tenantId],
        exact: false,
      });
      successToast({
        description: translate("common.deleteSuccess"),
      });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorDeleting"),
      });
    },
  });
};
